import React from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import map from 'lodash/map';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';

import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import Grid from '@material-ui/core/Grid';
import CollapsibleListItem from './collapsible-list-item';
import useStyles from './sub-risk.styles';

const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Kind of animal is required'),
    sex: Yup.string().required('Sex is required'),
    breed: Yup.string().nullable(),
    age: Yup.string().nullable(),
    originalCost: Yup.number().required('Original cost is required'),
    limit: Yup.number().required('Coverage limit is required'),
    description: Yup.string().nullable()
  });

const Animals4HFFAForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const animalItems =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.ANIMALS_4H_FFA)?.items || [];

  return (
    <SubRiskCollapsibleSection
      type="secondary"
      content={<div className={classes.headerText}>Animals (Under 4-H & FFA programs)</div>}
      initialOpen={false}
    >
      <FormSectionWrapper
        sectionTitle="Animals under 4-H & FFA programs"
        totalLabel="animals"
        totalCount={animalItems?.length || 0}
        emptyStateTitle="There are no animals added yet"
      >
        {/* list items */}
        {map(animalItems, (item, index) => {
          return (
            <CollapsibleListItem
              key={`animals4HFFA-${index}`}
              title={item.category}
              rightSideValues={[{ label: 'Limit', value: `$${item.limit}` }]}
              detailFields={[
                { name: 'kindOfAnimal', label: 'Kind of Animal', value: item.category, size: 4 },
                { name: 'sex', label: 'Sex', value: item.extendedDetails?.sex, size: 4 },
                { name: 'breed', label: 'Breed', value: item.extendedDetails?.breed, size: 4 },
                { name: 'age', label: 'Age', value: item.extendedDetails?.age, size: 4 },
                {
                  name: 'originalCost',
                  label: 'Original Cost',
                  value: item.extendedDetails?.originalCost ? `$${item.extendedDetails.originalCost}` : null,
                  size: 4
                },
                {
                  name: 'description',
                  label: 'Description (color, name, marks, etc.)',
                  value: item.description || 'Not provided',
                  size: 12
                }
              ]}
              onDelete={() => {
                const newItems = animalItems.filter((_, checkIndex) => checkIndex !== index);
                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter(
                    (item) => item.coverageType !== FarmScheduleCoverageType.ANIMALS_4H_FFA
                  ) || [];
                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.ANIMALS_4H_FFA,
                    items: newItems
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.ANIMALS_4H_FFA}`);
              }}
              disabled={disabled}
              keyPrefix={`animals4HFFA-${index}`}
            />
          );
        })}

        {/* add form */}
        <div className={classes.addFormContainer}>
          <Formik
            initialValues={{
              kindOfAnimal: undefined,
              sex: undefined,
              breed: undefined,
              age: undefined,
              originalCost: undefined,
              limit: undefined,
              description: undefined
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema()}
            onSubmit={(val, { resetForm }) => {
              const item = val;
              Object.keys(item).forEach((key) => {
                if (item[key] === '') {
                  item[key] = null;
                }
              });

              const newItem = {
                description: item.description,
                limit: item.limit,
                category: item.category,
                extendedDetails: {
                  sex: item.sex,
                  breed: item.breed,
                  age: item.age,
                  originalCost: item.originalCost
                }
              };

              const currentScheduleCoverages =
                getIn(values, fieldName)?.filter(
                  (item) => item.coverageType !== FarmScheduleCoverageType.ANIMALS_4H_FFA
                ) || [];
              const currentAnimalItems =
                getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.ANIMALS_4H_FFA)
                  ?.items || [];

              setFieldValue(`${fieldName}`, [
                ...currentScheduleCoverages,
                {
                  coverageType: FarmScheduleCoverageType.ANIMALS_4H_FFA,
                  items: [...currentAnimalItems, newItem]
                }
              ]);
              setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.ANIMALS_4H_FFA}`);

              resetForm();
            }}
          >
            {({ submitForm }) => {
              return (
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                  <Field
                    xs={4}
                    type="string"
                    id="category"
                    name="category"
                    mode="dark"
                    label="Kind of animal"
                    disabled={disabled}
                  />
                  <Field
                    xs={4}
                    type="select"
                    id="sex"
                    name="sex"
                    mode="dark"
                    label="Sex"
                    options={lookupsJson.farm.animalSex}
                    disabled={disabled}
                  />
                  <Field xs={4} type="string" id="breed" name="breed" mode="dark" label="Breed" disabled={disabled} />

                  <Field xs={4} type="string" id="age" name="age" mode="dark" label="Age" disabled={disabled} />
                  <Field
                    xs={4}
                    type="numeric"
                    id="originalCost"
                    name="originalCost"
                    mode="dark"
                    label="Original cost"
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disabled}
                  />
                  <Field
                    xs={4}
                    type="numeric"
                    id="limit"
                    name="limit"
                    mode="dark"
                    label="Coverage limit"
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disabled}
                  />

                  <Field
                    xs={12}
                    type="textarea"
                    id="description"
                    name="description"
                    mode="dark"
                    label="Description (color, name, marks, etc.)"
                    disabled={disabled}
                  />

                  <Grid container alignItems="center" className={classes.singleBottom}>
                    <Button
                      type="button"
                      mode="big"
                      variant="contained"
                      color="secondary"
                      className={classes.button}
                      disabled={disabled}
                      onClick={submitForm}
                    >
                      Add Item
                    </Button>
                  </Grid>
                </Grid>
              );
            }}
          </Formik>
        </div>
      </FormSectionWrapper>
    </SubRiskCollapsibleSection>
  );
};

Animals4HFFAForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

Animals4HFFAForm.defaultProps = {
  disabled: false
};

export default observer(Animals4HFFAForm);
